/** @file Weight rows of a skinned mesh (editable copies) and the one command a stroke or edit commits. */
import type { CommandCall, Id, MeshAtt } from '@pose2d/format/types';

type Influences = [Id, number][];

/** Copies in and the mesh.setWeights call out; rows are [bone id, weight] lists in the W2-29 normal form. */
export const weightRows = {
  /** A deep copy of a skinned mesh's weights, or null for a rigid mesh (paint needs mesh.setBinding skinned first). */
  of(mesh: MeshAtt): Influences[] | null {
    const g = mesh.geometry;
    return g.kind === 'skinned' ? g.weights.map((row) => row.map(([b, w]): [Id, number] => [b, w])) : null;
  },
  /** mesh.setWeights for every vertex (the command re-normalizes: rows already in normal form store unchanged). */
  call(attachment: Id, rows: readonly Influences[]): CommandCall {
    return { name: 'mesh.setWeights', input: { target: attachment, weights: rows.map((row) => row.map(([b, w]) => [b, w])) } };
  },
  /** The heaviest bone of the vertex nearest (x, y) within `radius` (world units), or null. */
  boneAt(rows: readonly Influences[], points: ArrayLike<number>, x: number, y: number, radius: number): Id | null {
    let best: Id | null = null;
    let bestD = radius;
    for (let v = 0; v < rows.length; v++) {
      const d = Math.hypot((points[2 * v] ?? NaN) - x, (points[2 * v + 1] ?? NaN) - y);
      if (d <= bestD && rows[v]?.[0] !== undefined) {
        best = rows[v]?.[0]?.[0] ?? null;
        bestD = d;
      }
    }
    return best;
  },
};
